import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, doc, updateDoc, onSnapshot, query, orderBy, deleteDoc } from 'firebase/firestore';

// Certificate Creation Form Component
export const CertificateCreationForm = ({
  newCertificate,
  setNewCertificate,
  handleAddCertificate
}) => {
  return (
    <div className="create-post">
      <h2>Add Certificate, Award, or Achievement</h2>
      <input
        type="text"
        placeholder="Title (e.g., AP Computer Science A, National Merit Scholar, Eagle Scout, SAT)"
        value={newCertificate.title}
        onChange={(e) => setNewCertificate({ ...newCertificate, title: e.target.value })}
        className="post-title-input"
      />
      <div className="project-type-selector">
        <label>Type:</label>
        <select
          value={newCertificate.type}
          onChange={(e) => setNewCertificate({ ...newCertificate, type: e.target.value })}
          className="project-type-dropdown"
        >
          <option value="">Select Type</option>
          <option value="exam">Examinations & Qualifications</option>
          <option value="award">Award/Honor</option>
          <option value="certificate">Certificate</option>
          <option value="competition">Competition</option>
          <option value="scholarship">Scholarship</option>
          <option value="badge">Badge/Credential</option>
          <option value="testscore">Test Score</option>
          <option value="other">Other</option>
        </select>
      </div>
      <input
        type="text"
        placeholder="Issuer/Organization (e.g., College Board, Parkland Animal Shelter, National Lab)"
        value={newCertificate.issuer}
        onChange={(e) => setNewCertificate({ ...newCertificate, issuer: e.target.value })}
        className="post-title-input"
      />
      <input
        type="text"
        placeholder="Grade/Place/Level (e.g., 1st Place, Gold Level, Score: 4, Score: 1200)"
        value={newCertificate.grade}
        onChange={(e) => setNewCertificate({ ...newCertificate, grade: e.target.value })}
        className="post-title-input"
      />
      <div className="form-group">
        <label>Date Received:</label>
        <input
          type="date"
          value={newCertificate.date}
          onChange={(e) => setNewCertificate({ ...newCertificate, date: e.target.value })}
          className="post-title-input"
        />
      </div>
      <textarea
        placeholder="Additional details about this achievement (optional)..."
        value={newCertificate.description}
        onChange={(e) => setNewCertificate({ ...newCertificate, description: e.target.value })}
        className="post-content-input"
        rows="3"
      />
      <button className="log-activity-button" onClick={handleAddCertificate}>Add Achievement</button>
    </div>
  );
};

// Certificate Display Component
export const CertificateDisplay = ({
  certificates,
  currentUser,
  handleToggleCertificateFeatured
}) => {
  const userCertificates = certificates.filter(cert => cert.userId === currentUser.uid);
  const pendingCertificates = userCertificates.filter(c => c.status !== 'approved');
  const approvedCertificates = userCertificates.filter(c => c.status === 'approved');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ title: '', type: '', issuer: '', grade: '', date: '', description: '' });
  
  const startEditing = (cert) => {
    setEditingId(cert.id);
    setEditData({
      title: cert.title || '',
      type: cert.type || '',
      issuer: cert.issuer || '',
      grade: cert.grade || '',
      date: cert.date || '',
      description: cert.description || ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEditing = async () => {
    if (!editingId) return;
    if (!editData.title.trim() || !editData.type || !editData.issuer.trim() || !editData.date) {
      alert('Please fill in all required fields.');
      return;
    }
    try {
      const certRef = doc(db, 'certificates', editingId);
      await updateDoc(certRef, {
        title: editData.title.trim(),
        type: editData.type,
        issuer: editData.issuer.trim(),
        grade: (editData.grade || '').trim(),
        date: editData.date,
        description: (editData.description || '').trim()
      });
      setEditingId(null);
    } catch (err) {
      console.error('Error updating certificate:', err);
      alert('Failed to update certificate. Please try again.');
    }
  };

  const handleDeleteCertificate = async (certificateId) => {
    const confirmDelete = window.confirm('Delete this certificate? This action cannot be undone.');
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'certificates', certificateId));
      if (editingId === certificateId) setEditingId(null);
    } catch (err) {
      console.error('Error deleting certificate:', err);
      alert('Failed to delete certificate.');
    }
  };
  
  if (userCertificates.length === 0) {
    return <p className="no-posts">No certificates or awards yet. Add your achievements above!</p>;
  }

  // Group certificates by type
  const certificatesByType = approvedCertificates.reduce((acc, cert) => {
    if (!acc[cert.type]) {
      acc[cert.type] = [];
    }
    acc[cert.type].push(cert);
    return acc;
  }, {});

  // Sort certificates within each type by date (newest first)
  Object.keys(certificatesByType).forEach(type => {
    certificatesByType[type].sort((a, b) => new Date(b.date) - new Date(a.date));
  });

  const typeLabels = {
    'exam': 'Examinations & Qualifications',
    'award': 'Awards & Honors',
    'certificate': 'Certificates',
    'competition': 'Competitions',
    'scholarship': 'Scholarships',
    'testscore': 'Test Scores',
  };

  return (
    <div className="certificates-container">
      <h2>Your Certificates & Awards</h2>
      <p>By default, certificates do not appear on your transcript. Use the Star button to add certificates to your transcript.</p>
      <div>
        {pendingCertificates.length > 0 && (
          <div className="awaiting-approval-section">
            <h3>Awaiting Parent Approval</h3>
            <div className="time-logs-list">
              {pendingCertificates.map(cert => {
                const isEditing = editingId === cert.id;
                return (
                  <div key={cert.id} className="individual-time-log-entry awaiting-approval">
                    <div className="individual-time-log-header">
                      <span className="individual-hours">{cert.issuer}</span>
                      <span className="individual-dates">{new Date(cert.date).toLocaleDateString()}</span>
                    </div>
                    <div className="individual-time-log-meta">
                      <span className="time-log-domain">{(cert.type || 'Other').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="awaiting-badge">Pending</span>
                    </div>
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          className="post-title-input"
                          placeholder="Title"
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                        />
                        <div className="form-row">
                          <div className="form-group half-width">
                            <label>Type</label>
                            <select
                              value={editData.type}
                              onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                              className="project-type-dropdown"
                            >
                              <option value="">Select Type</option>
                              <option value="exam">Examinations & Qualifications</option>
                              <option value="award">Award/Honor</option>
                              <option value="certificate">Certificate</option>
                              <option value="competition">Competition</option>
                              <option value="scholarship">Scholarship</option>
                              <option value="badge">Badge/Credential</option>
                              <option value="testscore">Test Score</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="form-group half-width">
                            <label>Grade/Level</label>
                            <input
                              type="text"
                              className="post-title-input"
                              value={editData.grade}
                              onChange={(e) => setEditData({ ...editData, grade: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group half-width">
                            <label>Issuer</label>
                            <input
                              type="text"
                              className="post-title-input"
                              value={editData.issuer}
                              onChange={(e) => setEditData({ ...editData, issuer: e.target.value })}
                            />
                          </div>
                          <div className="form-group half-width">
                            <label>Date</label>
                            <input
                              type="date"
                              className="post-title-input"
                              value={editData.date}
                              onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                            />
                          </div>
                        </div>
                        <textarea
                          className="proposal-edit-textarea"
                          rows="3"
                          placeholder="Additional details..."
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        />
                        <div className="proposal-actions">
                          <button className="proposal-save-btn" onClick={saveEditing}>Save</button>
                          <button className="proposal-cancel-btn" onClick={cancelEditing}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h5>{cert.title}</h5>
                        {cert.grade && (
                          <p className="individual-description">Grade/Level: {cert.grade}</p>
                        )}
                        {cert.description && (
                          <p className="individual-description">{cert.description}</p>
                        )}
                        <div className="proposal-actions">
                          <button className="proposal-edit-btn" onClick={() => startEditing(cert)}>Edit</button>
                          <button className="proposal-reject-btn" onClick={() => handleDeleteCertificate(cert.id)}>Delete</button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {Object.entries(certificatesByType).map(([type, certs]) => (
          <div key={type} className="certificate-type-section">
            <h3 className="certificate-type-title">
              {typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)}
            </h3>
            <div className="certificates-list">
              {certs.map(cert => (
                <div key={cert.id} className={`certificate-entry ${cert.featured ? 'featured' : ''}`}>
                  {editingId === cert.id ? (
                    <div className="certificate-edit-form">
                      <input
                        type="text"
                        className="post-title-input"
                        placeholder="Title"
                        value={editData.title}
                        onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      />
                      <div className="project-type-selector">
                        <label>Type:</label>
                        <select
                          className="project-type-dropdown"
                          value={editData.type}
                          onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                        >
                          <option value="">Select Type</option>
                          <option value="exam">Examinations & Qualifications</option>
                          <option value="award">Award/Honor</option>
                          <option value="certificate">Certificate</option>
                          <option value="competition">Competition</option>
                          <option value="scholarship">Scholarship</option>
                          <option value="badge">Badge/Credential</option>
                          <option value="testscore">Test Score</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        className="post-title-input"
                        placeholder="Issuer/Organization"
                        value={editData.issuer}
                        onChange={(e) => setEditData({ ...editData, issuer: e.target.value })}
                      />
                      <input
                        type="text"
                        className="post-title-input"
                        placeholder="Grade/Place/Level"
                        value={editData.grade}
                        onChange={(e) => setEditData({ ...editData, grade: e.target.value })}
                      />
                      <div className="form-group">
                        <label>Date Received:</label>
                        <input
                          type="date"
                          className="post-title-input"
                          value={editData.date}
                          onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                        />
                      </div>
                      <textarea
                        className="post-content-input"
                        rows="3"
                        placeholder="Additional details (optional)"
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      />
                        <div className="certificate-actions">
                          <button className="save-button" onClick={saveEditing}>Save</button>
                          <button className="cancel-button" onClick={cancelEditing}>Cancel</button>
                        </div>
                    </div>
                  ) : (
                    <>
                      <div className="certificate-header">
                        <div className="certificate-main">
                          <h4 className="certificate-title">{cert.title}</h4>
                          <span className="certificate-issuer">{cert.issuer}</span>
                          {cert.grade && (
                            <span className="certificate-grade">Grade/Level: {cert.grade}</span>
                          )}
                        </div>
                        <div className="certificate-meta">
                          <span className="certificate-date">
                            {new Date(cert.date).toLocaleDateString()}
                          </span>
                          {currentUser && cert.userId === currentUser.uid && (
                            <>
                              <button
                                className={`star-button ${cert.featured ? 'starred' : ''}`}
                                onClick={() => handleToggleCertificateFeatured && handleToggleCertificateFeatured(cert.id, cert.featured)}
                                title={cert.featured ? 'Remove from featured' : 'Add to featured'}
                              >
                                <i className={`fa-solid ${cert.featured ? 'fa-star' : 'fa-star'}`}></i>
                              </button>
                              {/* Allow delete always; hide edit once approved */}
                              {cert.status !== 'approved' && (
                                <button
                                  className="edit-button"
                                  onClick={() => startEditing(cert)}
                                  title="Edit certificate"
                                >
                                  Edit
                                </button>
                              )}
                              {cert.status === 'approved' ? (
                                <button
                                  className="star-button trash-button"
                                  onClick={() => handleDeleteCertificate(cert.id)}
                                  title="Delete certificate"
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </button>
                              ) : (
                                <button
                                  className="delete-button"
                                  onClick={() => handleDeleteCertificate(cert.id)}
                                  title="Delete certificate"
                                >
                                  Delete
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {cert.description && (
                        <div className="certificate-description">
                          {cert.description}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Certificate Transcript Display Component
export const CertificateTranscriptDisplay = ({
  certificates,
  currentUser,
  studentId
}) => {
  // Use studentId if provided (for teacher viewing student), otherwise use currentUser.uid (for student viewing own)
  const userId = studentId || currentUser?.uid;
  const userCertificates = certificates.filter(cert => cert.userId === userId && cert.status === 'approved');
  const featuredCertificates = userCertificates.filter(cert => cert.featured);
  
  if (featuredCertificates.length === 0) {
    return <p className="no-posts no-posts-padding">No featured certificates or awards yet.</p>;
  }

  // Group certificates by type
  const certificatesByType = featuredCertificates.reduce((acc, cert) => {
    if (!acc[cert.type]) {
      acc[cert.type] = [];
    }
    acc[cert.type].push(cert);
    return acc;
  }, {});

  // Sort certificates within each type by date (newest first)
  Object.keys(certificatesByType).forEach(type => {
    certificatesByType[type].sort((a, b) => new Date(b.date) - new Date(a.date));
  });

  const typeLabels = {
    'exam': 'Examinations & Qualifications',
    'award': 'Awards & Honors',
    'certificate': 'Certificates',
    'competition': 'Competitions',
    'scholarship': 'Scholarships',
    'testscore': 'Test Scores',
  };

  return (
    <div>
      {Object.entries(certificatesByType).map(([type, certs]) => (
        <div key={type} className="transcript-grade-section">
          <h4 className="transcript-grade-header">
            {typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)}
          </h4>
          
          <div className="transcript-time-logs-list">
            {certs.map(cert => (
              <div key={cert.id} className="transcript-time-log-entry">
                <div className="transcript-time-log-main">
                  <h5 className="transcript-time-log-activity">{cert.title}</h5>
                  {cert.grade && (
                    <span className="transcript-time-log-domain">Grade/Level: {cert.grade}</span>
                  )}
                </div>
                <div className="transcript-time-log-summary">
                  <span className="transcript-time-log-hours">{cert.issuer}</span>
                </div>
                {cert.description && (
                  <div className="transcript-time-log-dates">
                    {cert.description}
                  </div>
                )}
                <div className="transcript-time-log-dates">
                  {new Date(cert.date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Certificate Teacher Display Component (view-only, all certificates)
export const CertificateTeacherDisplay = ({
  certificates,
  currentUser,
  studentId
}) => {
  const userId = studentId || currentUser?.uid;
  const userCertificates = certificates.filter(cert => cert.userId === userId);

  if (userCertificates.length === 0) {
    return <p className="no-posts no-posts-padding">No certificates or awards yet.</p>;
  }

  const certificatesByType = userCertificates.reduce((acc, cert) => {
    if (!acc[cert.type]) acc[cert.type] = [];
    acc[cert.type].push(cert);
    return acc;
  }, {});

  Object.keys(certificatesByType).forEach(type => {
    certificatesByType[type].sort((a, b) => new Date(b.date) - new Date(a.date));
  });

  const typeLabels = {
    'exam': 'Examinations & Qualifications',
    'award': 'Awards & Honors',
    'certificate': 'Certificates',
    'competition': 'Competitions',
    'scholarship': 'Scholarships',
    'testscore': 'Test Scores',
  };

  return (
    <div>
      {Object.entries(certificatesByType).map(([type, certs]) => (
        <div key={type} className="transcript-grade-section">
          <h4 className="transcript-grade-header">
            {typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1)}
          </h4>
          <div className="transcript-time-logs-list">
            {certs.map(cert => (
              <div key={cert.id} className="transcript-time-log-entry">
                <div className="transcript-time-log-main">
                  <h5 className="transcript-time-log-activity">{cert.title}</h5>
                  {cert.grade && (
                    <span className="transcript-time-log-domain">Grade/Level: {cert.grade}</span>
                  )}
                </div>
                <div className="transcript-time-log-summary">
                  <span className="transcript-time-log-hours">{cert.issuer}</span>
                </div>
                {cert.description && (
                  <div className="transcript-time-log-dates">
                    {cert.description}
                  </div>
                )}
                <div className="transcript-time-log-dates">
                  {new Date(cert.date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Certificate Management Hook
export const useCertificateManagement = ({ currentUser, userProfile }) => {
  const [certificates, setCertificates] = useState([]);
  const [newCertificate, setNewCertificate] = useState({ 
    title: '', 
    type: '', 
    issuer: '', 
    date: '', 
    description: '',
    grade: ''
  });

  // Listen for Certificates
  useEffect(() => {
    if (!currentUser) {
      setCertificates([]);
      return;
    }

    const qCertificates = query(collection(db, 'certificates'), orderBy('date', 'desc'));
    const unsubscribeCertificates = onSnapshot(qCertificates, (snapshot) => {
      const certificatesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCertificates(certificatesData);
    }, (error) => console.error("Error fetching certificates:", error));

    return () => unsubscribeCertificates();
  }, [currentUser]);

  const handleAddCertificate = async () => {
    if (!newCertificate.title.trim() || !newCertificate.type || !newCertificate.issuer || !newCertificate.date || !currentUser) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      await addDoc(collection(db, 'certificates'), {
        userId: currentUser.uid,
        title: newCertificate.title.trim(),
        type: newCertificate.type,
        issuer: newCertificate.issuer.trim(),
        date: newCertificate.date,
        description: newCertificate.description.trim(),
        grade: newCertificate.grade.trim(),
        featured: false, // Default to not featured
        studentGrade: userProfile.grade, // Include student's grade from profile
        status: 'pending',
        timestamp: new Date().toISOString()
      });

      // Clear the form
      setNewCertificate({ 
        title: '', 
        type: '', 
        issuer: '', 
        date: '', 
        description: '',
        grade: ''
      });
    } catch (error) {
      console.error("Error adding certificate:", error.message);
      alert('Failed to add certificate. Please try again.');
    }
  };

  const handleToggleCertificateFeatured = async (certificateId, currentFeatured) => {
    try {
      // Enforce per-student limit: max 10 featured certificates
      if (!currentFeatured) {
        const thisCert = certificates.find(c => c.id === certificateId);
        const ownerId = thisCert?.userId || currentUser?.uid;
        const featuredCount = (certificates || []).filter(c => c.userId === ownerId && c.featured).length;
        if (featuredCount >= 10) {
          alert('You can only feature up to 10 certificates. Please unfeature another certificate first.');
          return;
        }
      }

      const certRef = doc(db, 'certificates', certificateId);
      await updateDoc(certRef, {
        featured: !currentFeatured
      });
      
      // Update local state
      setCertificates(prevCerts => 
        prevCerts.map(cert => 
          cert.id === certificateId 
            ? { ...cert, featured: !currentFeatured }
            : cert
        )
      );
    } catch (error) {
      console.error('Error toggling certificate featured status:', error);
      alert('Failed to update featured status');
    }
  };

  return {
    certificates,
    setCertificates,
    newCertificate,
    setNewCertificate,
    handleAddCertificate,
    handleToggleCertificateFeatured
  };
};

// Utility Functions
export const certificateUtils = {
  groupCertificatesByType: (certificates) => {
    const grouped = certificates.reduce((acc, cert) => {
      if (!acc[cert.type]) {
        acc[cert.type] = [];
      }
      acc[cert.type].push(cert);
      return acc;
    }, {});

    // Sort certificates within each type by date (newest first)
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => new Date(b.date) - new Date(a.date));
    });

    return grouped;
  },

  getTypeLabels: () => ({
    'exam': 'Examinations & Qualifications',
    'award': 'Awards & Honors',
    'certificate': 'Certificates',
    'competition': 'Competitions',
    'scholarship': 'Scholarships',
    'testscore': 'Test Scores',
  })
};

// Parent Approval Component for Certificates
export const CertificateParentApprovalList = ({ certificates, linkedStudentUid, noContainer = false }) => {
  const pendingCertificates = (certificates || []).filter(c => c.userId === linkedStudentUid && c.status !== 'approved');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ title: '', type: '', issuer: '', grade: '', date: '', description: '' });

  if (!linkedStudentUid) {
    return <p className="no-posts no-posts-padding">No student linked to this account.</p>;
  }

  if (pendingCertificates.length === 0) {
    return (
      <div className="create-post">
        <h3>Pending Certificates & Awards</h3>
        <p className="no-posts no-posts-padding">No certificates awaiting approval.</p>
      </div>
    );
  }

  const startEditing = (cert) => {
    setEditingId(cert.id);
    setEditData({
      title: cert.title || '',
      type: cert.type || '',
      issuer: cert.issuer || '',
      grade: cert.grade || '',
      date: cert.date || '',
      description: cert.description || ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEditing = async () => {
    if (!editingId) return;
    if (!editData.title.trim() || !editData.type || !editData.issuer.trim() || !editData.date) {
      alert('Please fill in all required fields.');
      return;
    }
    try {
      const certRef = doc(db, 'certificates', editingId);
      await updateDoc(certRef, {
        title: editData.title.trim(),
        type: editData.type,
        issuer: editData.issuer.trim(),
        grade: (editData.grade || '').trim(),
        date: editData.date,
        description: (editData.description || '').trim()
      });
      setEditingId(null);
    } catch (err) {
      console.error('Error updating certificate:', err);
      alert('Failed to update certificate. Please try again.');
    }
  };

  const approveCertificate = async (certificateId) => {
    try {
      const certRef = doc(db, 'certificates', certificateId);
      await updateDoc(certRef, { status: 'approved' });
    } catch (err) {
      console.error('Error approving certificate:', err);
      alert('Failed to approve certificate.');
    }
  };

  const rejectCertificate = async (certificateId) => {
    const ok = window.confirm('Reject and delete this certificate? This action cannot be undone.');
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'certificates', certificateId));
    } catch (err) {
      console.error('Error deleting certificate:', err);
      alert('Failed to delete certificate.');
    }
  };

  const typeLabels = certificateUtils.getTypeLabels();

  const content = (
    <>
      <h3>Pending Certificates & Awards</h3>
      <p className="section-description">Review and edit certificate details before approving.</p>
      <div className="proposal-list">
        {pendingCertificates.map(cert => {
          const isEditing = editingId === cert.id;
          return (
            <div key={cert.id} className="proposal-item">
              <div className="proposal-header">
                <div className="proposal-title">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      className="proposal-edit-input"
                    />
                  ) : (
                    cert.title
                  )}
                </div>
                <div className="proposal-meta">
                  <strong>{typeLabels[cert.type] || cert.type}</strong> · {new Date(cert.date).toLocaleDateString()}
                </div>
              </div>

              <div className="proposal-description">
                {isEditing ? (
                  <>
                    <div className="form-row">
                      <div className="form-group half-width">
                        <label>Type</label>
                        <select
                          value={editData.type}
                          onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                          className="project-type-dropdown"
                        >
                          <option value="">Select Type</option>
                          <option value="exam">Examinations & Qualifications</option>
                          <option value="award">Award/Honor</option>
                          <option value="certificate">Certificate</option>
                          <option value="competition">Competition</option>
                          <option value="scholarship">Scholarship</option>
                          <option value="badge">Badge/Credential</option>
                          <option value="testscore">Test Score</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group half-width">
                        <label>Issuer</label>
                        <input
                          type="text"
                          value={editData.issuer}
                          onChange={(e) => setEditData({ ...editData, issuer: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group half-width">
                        <label>Date</label>
                        <input
                          type="date"
                          value={editData.date}
                          onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                      <div className="form-group half-width">
                        <label>Grade/Level</label>
                        <input
                          type="text"
                          value={editData.grade}
                          onChange={(e) => setEditData({ ...editData, grade: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                    </div>
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="proposal-edit-textarea"
                      rows="3"
                      placeholder="Additional details..."
                    />
                  </>
                ) : (
                  cert.description || <em>No description provided</em>
                )}
              </div>

              <div className="proposal-footer">
                <div className="proposal-student">Pending</div>
                <div className="proposal-actions">
                  {isEditing ? (
                    <>
                      <button className="proposal-save-btn" onClick={saveEditing}>Save</button>
                      <button className="proposal-cancel-btn" onClick={cancelEditing}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="proposal-edit-btn" onClick={() => startEditing(cert)}>Edit</button>
                      <button className="proposal-approve-btn" onClick={() => approveCertificate(cert.id)}>Approve</button>
                      <button className="proposal-reject-btn" onClick={() => rejectCertificate(cert.id)}>Reject</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
  if (noContainer) {
    return content;
  }
  return (
    <div className="create-post">
      {content}
    </div>
  );
};

const certificateComponents = {
  CertificateCreationForm,
  CertificateDisplay,
  CertificateTranscriptDisplay,
  CertificateTeacherDisplay,
  useCertificateManagement,
  certificateUtils
};

export default certificateComponents;
